<template>
  <v-container>
    <v-layout row><v-flex>
      <v-card-text>
        <v-container>
          <h2 class="page-h">Showcase Feedback — Ani Aharoyan</h2>
          <p class="muted small">
            Every paid request shows up here. Paste the edited-video URL once it's ready
            and Ani's judge page picks it up automatically.
          </p>
          <p class="muted small">
            Ani's judge URL (bookmark for her):
            <code>{{ judgeUrl }}</code>
            <v-btn x-small outlined class="ml-2" @click="copyJudgeUrl">
              {{ copyNote || '📋 Copy' }}
            </v-btn>
          </p>

          <div v-if="loading" class="muted">Loading…</div>

          <table v-else class="sf-table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Dancer</th>
                <th>Piece</th>
                <th>Paid</th>
                <th>Edited video URL</th>
                <th>Feedback URL</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="f in rows" :key="f.id">
                <td>
                  <span class="sf-status" :class="'sf-status-' + f.status">
                    {{ statusLabel(f) }}
                  </span>
                </td>
                <td>
                  <div>{{ f.dancerName }}</div>
                  <div class="muted small">{{ f.dancerEmail }}</div>
                </td>
                <td>
                  <div>{{ f.pieceName }}</div>
                  <div class="muted small" v-if="f.isCompetitionEntry">competition</div>
                </td>
                <td class="num">${{ ((f.priceCents || 0) / 100).toFixed(0) }}</td>
                <td>
                  <input type="text" v-model="f.editedVideoUrl" class="url-input" placeholder="https://…" />
                </td>
                <td>
                  <a v-if="f.feedbackUrl" :href="f.feedbackUrl" target="_blank" class="sf-link">View →</a>
                  <span v-else class="muted small">—</span>
                </td>
                <td>
                  <v-btn
                    x-small color="primary"
                    :loading="saving === f.id"
                    :disabled="f.editedVideoUrl === (f._original || '')"
                    @click="saveVideo(f)"
                  >Save</v-btn>
                  <span v-if="savedFlash === f.id" class="saved-flash">✓</span>
                </td>
              </tr>
              <tr v-if="!rows.length">
                <td colspan="7" class="muted small">No feedback purchases yet.</td>
              </tr>
            </tbody>
          </table>
        </v-container>
      </v-card-text>
    </v-flex></v-layout>
  </v-container>
</template>

<script>
import { db } from '@/firebase'
import iabfAdminMixin from '@/utils/iabf-admin-mixin'


const JUDGE_KEY = 'iabf-2026-ani-feedback'

export default {
  mixins: [iabfAdminMixin],
  data () {
    return {
      loading: true,
      rows: [],
      unsub: null,
      saving: null,
      savedFlash: null,
      copyNote: ''
    }
  },
  computed: {
    judgeUrl () {
      return `${window.location.origin}/iabf/feedback-judge?key=${JUDGE_KEY}`
    }
  },
  async created () {
    this.$store.commit('setLayout', 'iabf-layout')
    this.unsub = db.collection(this.IABF.showcaseFeedback).onSnapshot(snap => {
      const rows = snap.docs.map(d => {
        const data = d.data()
        return {
          id: d.id,
          ...data,
          _original: data.editedVideoUrl || ''
        }
      })
      // Newest paid/created first; feedback-posted to the bottom.
      rows.sort((a, b) => {
        const orderOf = s => s === 'paid' ? 0 : s === 'created' ? 1 : 2
        const oa = orderOf(a.status)
        const ob = orderOf(b.status)
        if (oa !== ob) return oa - ob
        const ta = a.paidAt && a.paidAt.toMillis ? a.paidAt.toMillis() : 0
        const tb = b.paidAt && b.paidAt.toMillis ? b.paidAt.toMillis() : 0
        return tb - ta
      })
      this.rows = rows
      this.loading = false
    })
  },
  beforeDestroy () {
    if (typeof this.unsub === 'function') this.unsub()
  },
  methods: {
    statusLabel (f) {
      if (f.status === 'feedback-posted') return 'Done'
      if (f.status === 'paid' && f.editedVideoUrl) return 'In review'
      if (f.status === 'paid') return 'Awaiting video'
      if (f.status === 'created') return 'Unpaid'
      return f.status
    },
    async saveVideo (f) {
      this.saving = f.id
      try {
        await db.collection(this.IABF.showcaseFeedback).doc(f.id).update({
          editedVideoUrl: (f.editedVideoUrl || '').trim(),
          updatedAt: Date.now()
        })
        f._original = (f.editedVideoUrl || '').trim()
        this.savedFlash = f.id
        setTimeout(() => { if (this.savedFlash === f.id) this.savedFlash = null }, 1800)
      } catch (e) {
        console.error('Save failed:', e)
        // eslint-disable-next-line no-alert
        window.alert('Save failed: ' + (e.message || e))
      }
      this.saving = null
    },
    async copyJudgeUrl () {
      try {
        await navigator.clipboard.writeText(this.judgeUrl)
        this.copyNote = '✓ Copied'
      } catch (e) {
        this.copyNote = 'Copy failed'
      }
      setTimeout(() => { this.copyNote = '' }, 1800)
    }
  }
}
</script>

<style scoped>
.page-h { font-family: Quattrocento, Georgia, serif; font-size: 24px; }
.muted { color: #888; }
.small { font-size: 12px; }
.sf-table { width: 100%; border-collapse: collapse; font-size: 13px; margin-top: 12px; }
.sf-table th { text-align: left; padding: 8px; background: #faf8f3; font-size: 11px; text-transform: uppercase; color: #777; border-bottom: 1px solid #e6e1d4; }
.sf-table td { padding: 10px 8px; border-bottom: 1px solid #f3f1ed; vertical-align: middle; }
.sf-table td.num { font-family: 'Fanwood Text', Georgia, serif; }
.url-input { width: 100%; min-width: 220px; padding: 5px 8px; border: 1px solid #ccc; border-radius: 3px; font-size: 12px; font-family: ui-monospace, Menlo, monospace; }
.sf-status { padding: 1px 8px; border-radius: 10px; font-size: 11px; font-weight: 700; }
.sf-status-created { background: #f8d7da; color: #721c24; }
.sf-status-paid { background: #fff3cd; color: #856404; }
.sf-status-feedback-posted { background: #d4edda; color: #155724; }
.sf-link { color: #267E7D; text-decoration: none; font-weight: 600; }
.saved-flash { color: #2e7d32; font-weight: 700; margin-left: 6px; }
</style>
